import React, { useEffect, useRef, useState } from "react";

import "./Types.css";
import { useDispatch, useSelector } from "react-redux";
import {
  createType,
  deleteType,
  exportCSV,
  fetchAllTypes,
  updateType,
  uploadCSV,
} from "../../../redux/typeSlice";
import AppTable from "../../AppTable/AppTable";
import { scrollToTop, } from "../../../util/AppUtils";
import { showSnackbar } from "../../../redux/snackbarSlice";
import DeleteConfirmation from "../../Confirmations/DeleteConfirmation";
import AddBtn from "../AddBtn";
import UploadViaCsvBtn from "../UploadViaCsvBtn";
import ExportCsvBtn from "../ExportCsvBtn";
import { GetAuditTrailForImport } from "../../../util/getAuditTrailData";
import { fetchAllParts } from "../../../redux/partSlice";
import ImportCsvSuccessPopup from "../../ImportCsvSuccessPopup";
import Loader from "../../Loader";
import { TableActionGroup } from "../../Buttons";
import { updateUser } from "../../../redux/authSlice";
import { generateSHA256 } from "../../../util/generateSHA256";
import { getAuditTrailData } from "../../../util/getAuditTrailData";


const Types = ({ onSelectType, useTableInAuditTrail=false, auditTrailRow }) => {
  // Common
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const tableContainerRef = useRef();

  const dispatch = useDispatch();
  const { types, pagination } = useSelector((state) => state.type);
  const [importedInfo, setImportData] = useState(null);

  const columns = [
    {
      headName: "Name",
      field: "name",
      sort: true,
      filter: true,
      width: "150px",
      required: true,
    },
    {
      headName: "Order",
      field: "orders",
      sort: true,
      filter: true,
      required: true,
      width: "100px",
    },
    {
      headName: "Short Description",
      field: "short_description",
      sort: true,
      filter: true,
      // width: "200px",
    },
    { headName: "Module?", field: "is_module", width: "80px", sort: true, },
    { headName: "Document?", field: "is_procedure", width: "120px" },
    { headName: "Records?", field: "is_record", width: "80px", sort: true },
    { headName: "None?", field: "is_none", width: "80px", sort: true },
    { headName: "Action", field: "action", isAction: true },
  ];

  useEffect(() => {
    if(!useTableInAuditTrail)
    if (searchQuery.length) {
      //Add this condition to avoide unnecesory initial calls
      dispatch(fetchAllTypes({ searchQuery }));
    }
  }, [dispatch, searchQuery,useTableInAuditTrail]);
  // End Common

  const [showLoader, setShowLoader] = useState(false)
  const [newRow, setNewRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const fileInputRef = useRef(null);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRow((prev) => ({
      ...prev,
      [name]: name === "orders" ? parseInt(value, 10) || "" : value,
    }));
  };

  const snackbar = (message, type) => {
    dispatch(showSnackbar({ type: type, message: message }));
  };

  const handleCreateType = async () => {
    const newType = {...newRow}
    if (!newType.name.trim() || !newType.orders?.toString().trim()) {
      snackbar("Name and Order are required!", "error");
      return;
    }

    if (isNaN(newType.orders) || Number(newType.orders) < 0) {
      snackbar("Order must be a non-negative number!", "error");
      return;
    }

    if(newType.is_flowchart===true || newType.is_allow_record===true){
      newType.is_procedure = true
    }
    
    try {
      setShowLoader(true)
      await dispatch(createType({...newType,name:newType.name.trim()})).then(async (resp) => {
        setShowLoader(false)
        if (!resp.payload.success) {
          snackbar(resp.payload.error || resp.payload.message, "error");
        } else {
          await dispatch(fetchAllTypes({ searchQuery }));
          snackbar("Type created successfully!", "success");
          
          // If Module? is selected (is_module === true) and user is SuperAdmin, update permissions
          if (newType.is_module === true) {
            try {
              // Get logged-in user info from localStorage
              const loggedInUserInfo = localStorage.getItem("loggedinUserInfo");
              if (loggedInUserInfo) {
                const userInfo = JSON.parse(loggedInUserInfo);
                
                // Check if user has SuperAdmin rights
                const isSuperAdmin = 
                  userInfo.permission_to_edit?.SuperAdmin === true ||
                  userInfo.permission_to_edit?.Superadmin === true;

                if (isSuperAdmin) {
                  // Get type name from the created type
                  const typeName = newType.name.trim();

                  if (typeName) {
                    // Update module_type_permissions
                    const updatedModulePermissions = {
                      ...(userInfo.module_type_permissions || {}),
                      [typeName]: {
                        view: true,
                        edit: true,
                      },
                    };

                    // Update localStorage
                    const updatedUserInfo = {
                      ...userInfo,
                      module_type_permissions: updatedModulePermissions,
                    };
                    localStorage.setItem("loggedinUserInfo", JSON.stringify(updatedUserInfo));

                    // Prepare audit trail data
                    const hash = await generateSHA256(Math.random().toString());
                    const auditTrailData = await getAuditTrailData({ action: "UPDATE_USER" });

                    // Prepare user update payload
                    const userUpdatePayload = {
                      id: userInfo.id,
                      first_name: userInfo.first_name,
                      last_name: userInfo.last_name,
                      email: userInfo.email,
                      status: userInfo.status || "active",
                      role: userInfo.role || "",
                      user_information: userInfo.user_information || "",
                      note: userInfo.note || "",
                      resetpasswordtoken: "abc123xyz",
                      resetpasswordexpires: new Date().toISOString(),
                      module_type_permissions: updatedModulePermissions,
                      permission_to_edit: userInfo.permission_to_edit || {},
                      audit_trail_data: {
                        ...auditTrailData,
                        hash,
                        action: "UPDATE_USER",
                        comment: `Added permissions for newly created module type: ${typeName}`,
                        date: new Date().toISOString(),
                        parent_version: userInfo.version_id_ref || null,
                        version_content: {
                          rule_name: "Module Permissions Update",
                          modified_fields: {
                            module_type_permissions: {
                              previous: userInfo.module_type_permissions || {},
                              new: updatedModulePermissions,
                            },
                          },
                        },
                      },
                    };

                    // Call updateUser API
                    try {
                      await dispatch(updateUser(userUpdatePayload)).unwrap();
                    } catch (updateError) {
                      console.error("Error updating user permissions via API:", updateError);
                      // Don't block the flow if API update fails
                    }
                  }
                }
              }
            } catch (error) {
              console.error("Error updating user permissions for new module type:", error);
              // Don't block the flow if permission update fails
            }
          }
          
          setNewRow(null);
        }
      });
    } catch (err) {
      snackbar("Type creation failed!", "error");
    }
  };

  const handleDelete = (id) => {
    DeleteConfirmation(async () => {
      try {
        setShowLoader(true);
        await dispatch(deleteType(id)).unwrap();
        setShowLoader(false);
        snackbar("Type deleted successfully!", "success");
        dispatch(fetchAllTypes({ searchQuery }));
      } catch (error) {
        setShowLoader(false);
        snackbar(
          error?.message || error?.error || "Type deletion failed!",
          "error"
        );
      }
    }, "Are you sure you want to delete this type?");
  };

  const handleAddRow = () => {
    // Check if a row is already being edited
    if (editingRowId !== null) {
      snackbar(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }

    // Check if a new row is already being added
    if (newRow !== null) {
      snackbar(
        "Please save or cancel the current row before adding or editing another one.",
        "error"
      );
      return;
    }

    setNewRow({
      name: "",
      orders: "",
      short_description: "",
      is_module: false,
      is_procedure: false,
      is_flowchart: false,
      is_allow_record:false,
      is_record:false,
      is_none:true,
    });
    scrollToTop(tableContainerRef);
  };

  const handleFileChange = async (e, actionType) => {
      const file = e.target.files[0];
      if (!file) return;
  
      // Reset the file input to allow same file upload next time
      e.target.value = "";
   
      const auditTrailData = await GetAuditTrailForImport()
  
      try {
        setShowLoader(true)
        const response = await dispatch(
          uploadCSV({ csvFile: file, auditTrailData })
        );
        setShowLoader(false)
        if(response?.payload?.success){
          snackbar(
            response.payload.message,
            "success"
          );
        }
        if (response?.payload?.success) {
          dispatch(fetchAllTypes({ searchQuery }));
        }
        setImportData(response?.payload);
      } catch (error) {
        showSnackbar("Error while importing CSV: " + (error.message || "Unknown error"), "error");
      }
    
      
    };
  const handleExportCSV = () => {
    setShowLoader(true)
    dispatch(exportCSV()).then((res) => {
      setShowLoader(false)
      if (exportCSV.fulfilled.match(res)) {
        snackbar("Types CSV export completed!", "success");
      } else {
        snackbar("Error exporting CSV: " + res.payload, "error");
      }
    });
  };

  return (
    <>
      <AppTable
        ref={tableContainerRef}
        columnsData={useTableInAuditTrail ? columns.map(d=>({...d,sort:false})).filter(a=>a.field!=="action") : columns}
        showSearchInput={useTableInAuditTrail ? false : true}
        paginate={useTableInAuditTrail ? false : true}
        pagination={{
          limit: [limit, setLimit],
          offset: [offset, setOffset],
          pagination,
          maxVsbleButtons: 7,
          justifyContent: "center",
          paginationPosition: {
            position: "center",
            leftContent: <AddBtn onClick={handleAddRow} />,
            rightContent: (
              <>
                <UploadViaCsvBtn
                  fileInputRef={fileInputRef}
                  handleFileChange={handleFileChange}
                />{" "}
                &nbsp;
                <ExportCsvBtn handleExportCSV={handleExportCSV} />
              </>
            ),
          },
          paginationShadow: true,
        }}
        onFilterSortHandler={setSearchQuery}
        usingInPopup={true}
      >
        {
          !useTableInAuditTrail &&<>
            {newRow && (
              <tr>
                <td>
                  <input
                    type="text"
                    name="name"
                    value={newRow.name}
                    onChange={handleInputChange}
                    required
                  />
                </td>
                <td>
                  <input
                    type="number"
                    name="orders"
                    value={newRow.orders}
                    onChange={handleInputChange}
                    required
                  />
                </td>
                <td>
                  <input
                    type="text"
                    name="short_description"
                    value={newRow.short_description}
                    onChange={handleInputChange}
                  />
                </td>
                <td>
                  <label>
                    <input
                      type="radio"
                      name="typeSelector"
                      style={{ width: "14px", height: "15px" }}
                      checked={newRow.is_module}
                      onChange={() =>
                        setNewRow({
                          ...newRow,
                          is_module: true,
                          is_procedure: false,
                          is_flowchart: false,
                          is_allow_record:false,
                          is_record: false,
                          is_none:false,
                        })
                      }
                    />
                    <span style={{ marginTop: "5px" }}>Yes</span>
                  </label>
                </td>
                <td>
                  <label>
                    <input
                      type="radio"
                      name="typeSelector"
                      checked={newRow.is_procedure}
                      style={{ width: "14px", height: "15px" }}
                      onChange={() =>
                        setNewRow({
                          ...newRow,
                          is_module: false,
                          is_procedure: true,
                          is_flowchart: false,
                          is_allow_record:false,
                          is_record: false,
                          is_none:false,
                        })
                      }
                    />
                    <span style={{ marginTop: "5px" }}>Document</span>
                  </label>      
                  <label>
                    <input
                      type="radio"
                      name="typeSelector"
                      checked={newRow.is_flowchart}
                      style={{ width: "14px", height: "15px" }}
                      onChange={() =>
                        setNewRow({
                          ...newRow,
                          is_module: false,
                          is_procedure: false,
                          is_flowchart: true,
                          is_allow_record:false,
                          is_record: false,
                          is_none:false,
                        })
                      }
                    />
                    <span style={{ marginTop: "5px" }}>Document and Flowchart</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="typeSelector"
                      checked={newRow.is_allow_record}
                      style={{ width: "14px", height: "15px" }}
                      onChange={() =>
                        setNewRow({
                          ...newRow,
                          is_module: false,
                          is_procedure: false,
                          is_flowchart: false,
                          is_allow_record:true,
                          is_record: false,
                          is_none:false,
                        })
                      }
                    />
                    <span style={{ marginTop: "5px" }}>Document with Records</span>
                  </label>
                </td>
                <td>
                  <label>
                    <input
                      type="radio"
                      name="typeSelector"
                      checked={newRow.is_record}
                      style={{ width: "14px", height: "15px" }}
                      onChange={() =>
                        setNewRow({
                          ...newRow,
                          is_module: false,
                          is_procedure: false,
                          is_flowchart: false,
                          is_allow_record:false,
                          is_record: true,
                          is_none:false,
                        })
                      }
                    />
                    <span style={{ marginTop: "5px" }}>Yes</span>
                  </label>
                </td>
                <td>
                  <label>
                    <input
                      type="radio"
                      name="typeSelector"
                      checked={newRow.is_none}
                      style={{ width: "14px", height: "15px" }}
                      onChange={() =>
                        setNewRow({
                          ...newRow,
                          is_module: false,
                          is_procedure: false,
                          is_flowchart: false,
                          is_allow_record:false,
                          is_record: false,
                          is_none:true,
                        })
                      }
                    />
                    <span style={{ marginTop: "5px" }}>Yes</span>
                  </label>
                </td>
                <td>
                  <TableActionGroup
                    mode="editing"
                    onSave={handleCreateType}
                    onCancel={() => setNewRow(null)}
                  />
                </td>
              </tr>
            )}
            {types.map((row,index) => {
              const isEditing = editingRowId === row.type_id

              let disableOptions = !isEditing
              let documentShouldNotBeEnabled = !isEditing
              

              let setNone = false 
              const checker = (key) => isEditing ? !!editedRow[key] : !!row[key];

              if(isEditing){
                if(row.is_module && row.used_in_module){
                  disableOptions = true
                  documentShouldNotBeEnabled = true
                }
                else if(row.is_procedure && row.is_flowchart){
                    disableOptions = true
                  // if(row.used_in_procedure){}
                  if(row.used_in_flowchart){
                    documentShouldNotBeEnabled = true
                  }
                }else if(row.is_procedure && !row.is_flowchart){
                  if(row.used_in_procedure){
                    disableOptions = true
                    documentShouldNotBeEnabled = false
                  }
                }else{
                  disableOptions = false
                  documentShouldNotBeEnabled = false
                }
              }

              if(
                !row.is_module && 
                !row.is_procedure && 
                !row.is_flowchart && 
                !row.is_allow_record && 
                !row.is_record && 
                !row.is_none)
              {
                setNone = true;
              }
              return (
                <tr key={row.type_id}>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedRow.name}
                        disabled={true}
                        style={{
                          opacity: 0.9,
                          border: "1px solid #ebebeb",
                        }}
                        onChange={(e) =>
                          setEditedRow({
                            ...editedRow,
                            name: e.target.value,
                          })
                        }
                      />
                    ) : (
                      row.name
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editedRow.orders}
                        onChange={(e) =>
                          setEditedRow({
                            ...editedRow,
                            orders: parseInt(e.target.value, 10),
                          })
                        }
                      />
                    ) : (
                      row.orders
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedRow.short_description}
                        onChange={(e) =>
                          setEditedRow({
                            ...editedRow,
                            short_description: e.target.value,
                          })
                        }
                      />
                    ) : (
                      row.short_description
                    )}
                  </td>
                  <td>
                    <label>
                      <input
                        type="radio"
                        style={{ width: "14px", height: "15px" }}
                        checked={checker('is_module')}
                        name={`editType-${row.type_id}`}
                        disabled={disableOptions}
                        onChange={() =>
                          setEditedRow(prev=>({...prev, 
                            is_module: true,
                            is_procedure: false,
                            is_flowchart: false,
                            is_allow_record:false,
                            is_record: false,
                            is_none:false,
                          }))
                        }
                      />
                      <span>Yes</span>
                    </label>
                  </td>
                  <td>
                    <label>
                        <input
                          type="radio"
                          name={`editType-${row.type_id}`}
                          checked={checker('is_procedure') && !checker('is_flowchart') && !checker('is_allow_record')}
                          style={{ width: "14px", height: "15px" }}
                          disabled={documentShouldNotBeEnabled}
                          onChange={() =>
                            setEditedRow(prev=>({...prev, 
                              is_module: false,
                              is_procedure: true,
                              is_flowchart: false,
                              is_allow_record:false,
                              is_record: false,
                              is_none:false,
                            }))
                          }
                        />
                        <span style={{ marginTop: "5px" }}>Document</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`editType-${row.type_id}`}
                        checked={checker('is_flowchart')}
                        style={{ width: "14px", height: "15px" }}
                        disabled={documentShouldNotBeEnabled}
                        onChange={() =>
                          setEditedRow(prev=>({...prev, 
                            is_module: false,
                            is_procedure: false,
                            is_flowchart: true,
                            is_allow_record:false,
                            is_record: false,
                            is_none:false,
                          }))
                        }
                      />
                      <span style={{ marginTop: "5px" }}>Document and Flowchart</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`editType-${row.type_id}`}
                        checked={checker('is_allow_record')}
                        style={{ width: "14px", height: "15px" }}
                        disabled={documentShouldNotBeEnabled}
                        onChange={() =>
                          setEditedRow(prev=>({...prev, 
                            is_module: false,
                            is_procedure: false,
                            is_flowchart: false,
                            is_allow_record:true,
                            is_record: false,
                            is_none:false,
                          }))
                        }
                      />
                      <span style={{ marginTop: "5px" }}>Document with Records</span>
                    </label>
                  </td>
                  <td>
                    <label>
                      <input
                        type="radio"
                        name={`editType-${row.type_id}`}
                        checked={checker('is_record')}
                        style={{ width: "14px", height: "15px" }}
                        disabled={disableOptions}
                        onChange={() =>
                          setEditedRow(prev=>({...prev, 
                            is_module: false,
                            is_procedure: false,
                            is_flowchart: false,
                            is_allow_record:false,
                            is_record: true,
                            is_none:false,
                          }))
                        }
                      />
                      <span style={{ marginTop: "5px" }}>Yes</span>
                    </label>
                  </td>
                  <td>
                    <label>
                        <input
                          type="radio"
                          style={{ width: "14px", height: "15px" }}
                          name={`editType-${row.type_id}`}
                          checked={isEditing ? !!editedRow.is_none : (setNone || !!row.is_none)}
                          disabled={disableOptions}
                          onChange={() =>
                            setEditedRow(prev=>({...prev, 
                              is_module: false,
                              is_procedure: false,
                              is_flowchart: false,
                              is_allow_record:false,
                              is_record: false,
                              is_none:true,
                            }))
                          }
                        />
                        <span style={{ marginTop: "5px" }}>Yes</span>
                      </label>
                  </td>
                  <td>
                    {isEditing ? (
                      <TableActionGroup
                        mode="editing"
                        onSave={async () => {
                          if (
                            !editedRow.name.trim() ||
                            !editedRow.orders?.toString().trim()
                          ) {
                            snackbar("Name and Order are required!", "error");
                            return;
                          }

                          if (
                            isNaN(editedRow.orders) ||
                            Number(editedRow.orders) < 0
                          ) {
                            snackbar(
                              "Order must be a non-negative number!",
                              "error"
                            );
                            return;
                          }

                          try {
                            setShowLoader(true)
                            await dispatch(
                              updateType({
                                id: row.type_id,
                                data: editedRow,
                                parent_version: row.version_id_ref,
                              })
                            );
                            setShowLoader(false)
                            await dispatch(fetchAllTypes({ searchQuery }));
                            snackbar("Type updated successfully!", "success");
                            setNewRow(null);
                          } catch (error) {
                            snackbar(error?.message, "error");
                          }
                          setEditingRowId(null);
                          setEditedRow({});
                        }}
                        onCancel={() => {
                          setEditingRowId(null);
                          setEditedRow({});
                        }}
                      />
                    ) : (
                      <TableActionGroup
                        mode="view"
                        showEdit={true}
                        showDelete={!onSelectType}
                        showSelect={!!onSelectType}
                        disableEdit={editingRowId !== null || newRow !== null}
                        disableDelete={
                          row.used_in_part ||
                          editingRowId !== null ||
                          newRow !== null
                        }
                        disableSelect={editingRowId !== null || newRow !== null}
                        onEdit={() => {
                          // Check if a row is already being edited
                          if (editingRowId !== null) {
                            snackbar(
                              "Please save or cancel the current row before adding or editing another one.",
                              "error"
                            );
                            return;
                          }

                          // Check if a new row is already being added
                          if (newRow !== null) {
                            snackbar(
                              "Please save or cancel the current row before adding or editing another one.",
                              "error"
                            );
                            return;
                          }
                          setEditingRowId(row.type_id);
                          setEditedRow({ ...row, is_none: row.is_none || setNone });
                        }}
                        onDelete={() => {
                          // Check if a row is already being edited
                          if (editingRowId !== null) {
                            snackbar(
                              "Please save or cancel the current row before adding or editing another one.",
                              "error"
                            );
                            return;
                          }

                          // Check if a new row is already being added
                          if (newRow !== null) {
                            snackbar(
                              "Please save or cancel the current row before adding or editing another one.",
                              "error"
                            );
                            return;
                          }
                          handleDelete(row.type_id);
                        }}
                        onSelect={() => onSelectType(row)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </>
        }
        {
          useTableInAuditTrail && <>{auditTrailRow}</>
        }
        
      </AppTable>

      {
        importedInfo && (
          <ImportCsvSuccessPopup
            importedInfo={importedInfo}
            setNullInportData={setImportData}
          />
        )
      }

    {
      showLoader && <Loader/>
    }
    </>
  );
};

export default Types;
